import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { ProjectModelsIcon } from "@common/assets/projectModelsIcon";
import { UseCases } from "@planzone/core";
import { Safeguard } from "@scenes/safeguard";
import { FolderIcon } from "@common/folderIcon";
import { VisibilityWatcher } from "@common/visibilityWatcher";
import { useModal } from "@root/client/modal";
import { Props } from "./types";
import { ArrowIcon } from "./arrowIcon";
import styles from "./styles.scss";

function Menu({ project }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();

  return (
    <Safeguard
      useCases={[
        UseCases.duplicateProject,
        UseCases.editProject,
        UseCases.createProjectModelFromProject,
        UseCases.transferProjects,
        UseCases.removeProject,
        UseCases.createProjectStrategicAxis,
        UseCases.retrieveProjectLogs,
        UseCases.retrieveSecondaryCriteriaTemplate,
      ]}
    >
      {({
        canEditProject,
        canCreateProjectModelFromProject,
        canTransferProjects,
        canRemoveProject,
        canCreateProjectStrategicAxis,
        canRetrieveProjectLogs,
        canRetrieveSecondaryCriteriaTemplate,
        canDuplicateProject,
      }) =>
        canEditProject ||
        canCreateProjectModelFromProject ||
        canTransferProjects ||
        canRemoveProject ||
        canRetrieveProjectLogs ||
        canCreateProjectStrategicAxis ||
        canRetrieveSecondaryCriteriaTemplate ||
        canDuplicateProject ? (
          <VisibilityWatcher>
            {(isVisible, show, hide) => (
              <div
                className={classNames(styles.menu, {
                  [styles.open]: isVisible,
                })}
                onClick={isVisible ? hide : show}
              >
                <div className={styles.main}>
                  <div className={styles.wrapper}>
                    {!project.isTemplate ? (
                      <FolderIcon className={styles.icon} isOpen />
                    ) : (
                      <ProjectModelsIcon
                        className={classNames(
                          styles.icon,
                          styles.projectModelIcon,
                        )}
                      />
                    )}
                    <span className={styles.name}>{project.name}</span>
                  </div>
                  <ArrowIcon className={styles.toggleArrow} />
                </div>
                {isVisible && (
                  <div className={styles.list}>
                    {canEditProject && (
                      <p
                        className={styles.item}
                        onClick={() =>
                          pushView({
                            data: { project },
                            key: UseCases.retrieveProject,
                          })
                        }
                      >
                        {t("projectPage.menu.projectDetails")}
                      </p>
                    )}
                    {!project.isTemplate &&
                      canCreateProjectModelFromProject && (
                        <p
                          className={styles.item}
                          onClick={() =>
                            pushView({
                              data: project,
                              key: UseCases.createProjectModelFromProject,
                            })
                          }
                        >
                          {t("projectPage.menu.createProjectModel")}
                        </p>
                      )}
                    {canEditProject && (
                      <p
                        className={styles.item}
                        onClick={() =>
                          pushView({
                            data: project,
                            key: UseCases.retrieveTaskStatuses,
                          })
                        }
                      >
                        {t("projectPage.menu.projectStatus")}
                      </p>
                    )}
                    {canCreateProjectStrategicAxis && (
                      <p
                        className={styles.item}
                        onClick={() =>
                          pushView({
                            data: project,
                            key: UseCases.createProjectStrategicAxis,
                          })
                        }
                      >
                        {t("projectPage.menu.strategicImpact")}
                      </p>
                    )}
                    {canTransferProjects && !project.isTemplate && (
                      <p
                        className={styles.item}
                        onClick={() =>
                          pushView({
                            data: { zone: project.zone, projects: [project] },
                            key: UseCases.transferProjects,
                          })
                        }
                      >
                        {t("projectPage.menu.move")}
                      </p>
                    )}
                    {canDuplicateProject && (
                      <p
                        className={styles.item}
                        onClick={() =>
                          pushView({
                            data: { zone: project.zone, project },
                            key: UseCases.duplicateProject,
                          })
                        }
                      >
                        {t("projectPage.menu.duplicate")}
                      </p>
                    )}
                    {canRetrieveSecondaryCriteriaTemplate && (
                      <p
                        className={styles.item}
                        onClick={() =>
                          pushView({
                            data: {
                              namespace: project.uuid,
                              scope: "project",
                            },
                            key: UseCases.retrieveSecondaryCriteriaTemplate,
                          })
                        }
                      >
                        {t("projectPage.menu.secondaryCriteria")}
                      </p>
                    )}
                    {/*{canRetrieveProjectLogs && !project.isTemplate && (*/}
                    {/*  <p*/}
                    {/*    className={styles.item}*/}
                    {/*    onClick={() =>*/}
                    {/*      pushView({*/}
                    {/*        data: project.uuid,*/}
                    {/*        key: UseCases.retrieveProjectLogs,*/}
                    {/*      })*/}
                    {/*    }*/}
                    {/*  >*/}
                    {/*    {t("projectPage.menu.logs")}*/}
                    {/*  </p>*/}
                    {/*)}*/}
                    {canRemoveProject && (
                      <p
                        className={classNames(styles.item, styles.dangerous)}
                        onClick={() =>
                          pushView({
                            data: project,
                            key: UseCases.removeProject,
                          })
                        }
                      >
                        {t("projectPage.menu.remove")}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </VisibilityWatcher>
        ) : (
          <div className={classNames(styles.menu, styles.inactive)}>
            <div className={styles.main}>
              <div className={styles.wrapper}>
                <FolderIcon className={styles.icon} isOpen />
                <span className={styles.name}>{project.name}</span>
              </div>
              <div className={styles.toggleArrow} />
            </div>
          </div>
        )
      }
    </Safeguard>
  );
}

export { Menu };
